/**
 * CL-ENG-INVITE-001 — Engineer invite landing, defaults, readiness, and orientation (pure model).
 */

import {
  buildDefaultCollaboratorPermissions,
  normalizeCollaboratorPermissions,
} from "./projectCollaboratorPermissions";
import { buildEngineerIdentityInviteGate } from "./canonicalContactIdentityModel";
import { countDrawingsInPackageForCollaborator } from "./drawingEntitlementModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

function uniqueStrings(values = []) {
  const out = [];
  const seen = new Set();
  (Array.isArray(values) ? values : []).forEach((v) => {
    const s = safeStr(v);
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  });
  return out;
}

function normEmail(value) {
  return safeStr(value).toLowerCase();
}

const ACCESS_SCOPE_LABELS = {
  full_project: "Full project / archive access",
  concept_only: "Concepts only",
  permit_drawings: "Permit drawings only",
  shared_files: "Shared files only",
};

function accessScopeLabel(scopeKey) {
  const key = safeStr(scopeKey).toLowerCase();
  return ACCESS_SCOPE_LABELS[key] || key.replace(/_/g, " ") || "—";
}

function roleLabel(roleKey) {
  const key = safeStr(roleKey).toLowerCase();
  if (key === "engineer") return "Engineer";
  if (key === "designer") return "Designer";
  if (key === "client") return "Client";
  return key ? key.replace(/_/g, " ") : "—";
}

function resolvePortalBaseUrl(origin = "") {
  return safeStr(origin).replace(/\/$/, "");
}

function resolveClientPortalUrl({ portalBaseUrl = "", accessCode = "" } = {}) {
  const base = resolvePortalBaseUrl(portalBaseUrl);
  const code = safeStr(accessCode);
  if (base && code) return `${base}/client-portal?invite=${encodeURIComponent(code)}`;
  if (base) return `${base}/client-portal`;
  return code ? `/client-portal?invite=${encodeURIComponent(code)}` : "/client-portal";
}

export const ENGINEER_RECOMMENDED_ACCESS_SCOPE = "shared_files";

export const PORTAL_INVITE_LANDING_PROJECT_KEY = "portalInviteLandingProjectV1";

export function isEngineerCollaboratorRole(role) {
  return safeStr(role).toLowerCase() === "engineer";
}

/**
 * Recommended defaults when applying Engineer role intentionally.
 * Does not silently overwrite — caller applies only on explicit action.
 */
export function buildEngineerCollaboratorDefaults({ accessScope = ENGINEER_RECOMMENDED_ACCESS_SCOPE } = {}) {
  const scope = safeStr(accessScope) || ENGINEER_RECOMMENDED_ACCESS_SCOPE;
  return {
    role: "engineer",
    accessScope: scope,
    permissions: buildDefaultCollaboratorPermissions({ role: "engineer", accessScope: scope }),
  };
}

export function engineerPermissionsMatchDefaults(row = {}) {
  const scope = safeStr(row?.accessScope) || ENGINEER_RECOMMENDED_ACCESS_SCOPE;
  const current = normalizeCollaboratorPermissions(row?.permissions, {
    role: "engineer",
    accessScope: scope,
  });
  const defaults = buildDefaultCollaboratorPermissions({ role: "engineer", accessScope: scope });
  return Object.keys(defaults).every((key) => current[key] === defaults[key]);
}

export function summarizeEngineerPermittedActions(permissions = {}, context = {}) {
  const perms = normalizeCollaboratorPermissions(permissions, {
    role: context.role || "engineer",
    accessScope: context.accessScope || ENGINEER_RECOMMENDED_ACCESS_SCOPE,
  });
  const lines = [];
  if (perms.canViewPlanning) lines.push("View shared project / planning information");
  if (perms.canViewPhotos) lines.push("View project photos");
  if (perms.canReviewDrawings) lines.push("Review and comment on drawings");
  if (perms.canUploadFiles) lines.push("Upload revisions and engineering documents");
  if (perms.canEditEstimate) lines.push("Edit planning estimates");
  if (perms.canPublishEstimate) lines.push("Publish estimates to client");
  if (perms.canLogTime || perms.canViewTimeLog) lines.push("Time Log access");
  if (perms.canManageCollaborators) lines.push("Manage collaborators");
  if (!lines.length) lines.push("Limited access — confirm permissions before sending");
  return lines;
}

/**
 * Safe post-invite project landing. Never trusts an arbitrary redirect.
 * @returns {{ ok: boolean, projectId: string|null, reason: string, path: string|null }}
 */
export function resolvePostInviteProjectLanding({
  inviteProjectIds = [],
  permittedProjectIds = [],
  requestedProjectId = "",
} = {}) {
  const inviteIds = uniqueStrings(inviteProjectIds);
  const permitted = uniqueStrings(permittedProjectIds);
  const requested = safeStr(requestedProjectId);

  if (requested) {
    if (permitted.includes(requested)) {
      return {
        ok: true,
        projectId: requested,
        reason: "validated_request",
        path: buildClientPortalProjectPath(requested),
      };
    }
    return { ok: false, projectId: null, reason: "unauthorized_redirect", path: null };
  }

  if (inviteIds.length === 1) {
    const only = inviteIds[0];
    if (permitted.includes(only)) {
      return {
        ok: true,
        projectId: only,
        reason: "single_invite_project",
        path: buildClientPortalProjectPath(only),
      };
    }
    return { ok: false, projectId: null, reason: "invite_project_not_permitted", path: null };
  }

  return { ok: true, projectId: null, reason: "multi_or_none", path: null };
}

export function buildClientPortalProjectPath(projectId) {
  const id = safeStr(projectId);
  if (!id) return null;
  return `/client-portal/projects/${encodeURIComponent(id)}`;
}

export function writePortalInviteLandingProjectId(projectId, emailLower = "", role = "engineer") {
  const id = safeStr(projectId);
  const email = normEmail(emailLower);
  const roleKey = safeStr(role).toLowerCase() || "engineer";
  try {
    // Only engineer invites store a deep-link intent — preserves client / dual-service landing.
    if (!id || !email || roleKey !== "engineer") {
      sessionStorage.removeItem(PORTAL_INVITE_LANDING_PROJECT_KEY);
      return;
    }
    sessionStorage.setItem(
      PORTAL_INVITE_LANDING_PROJECT_KEY,
      JSON.stringify({ projectId: id, emailLower: email, role: roleKey })
    );
  } catch {
    /* ignore */
  }
}

export function peekPortalInviteLandingIntent() {
  try {
    const raw = sessionStorage.getItem(PORTAL_INVITE_LANDING_PROJECT_KEY);
    if (!raw) return null;
    // Legacy plain project id (pre email-binding) — treat as unsafe and ignore.
    if (!String(raw).trim().startsWith("{")) return null;
    const parsed = JSON.parse(raw);
    const projectId = safeStr(parsed?.projectId);
    const emailLower = normEmail(parsed?.emailLower);
    const role = safeStr(parsed?.role).toLowerCase();
    if (!projectId || !emailLower) return null;
    return { projectId, emailLower, role: role || "engineer" };
  } catch {
    return null;
  }
}

export function consumePortalInviteLandingProjectId() {
  const intent = peekPortalInviteLandingIntent();
  try {
    sessionStorage.removeItem(PORTAL_INVITE_LANDING_PROJECT_KEY);
  } catch {
    /* ignore */
  }
  return intent?.projectId || "";
}

export function peekPortalInviteLandingProjectId() {
  return peekPortalInviteLandingIntent()?.projectId || "";
}

export function clearPortalInviteLandingProjectId() {
  try {
    sessionStorage.removeItem(PORTAL_INVITE_LANDING_PROJECT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Apply invite deep-link only for the matching engineer email + permitted project.
 */
export function resolvePortalInviteLandingForViewer({
  intent = null,
  viewerEmail = "",
  permittedProjectIds = [],
} = {}) {
  if (!intent?.projectId) {
    return { ok: true, projectId: null, reason: "none", path: null };
  }
  if (safeStr(intent.role).toLowerCase() && safeStr(intent.role).toLowerCase() !== "engineer") {
    return { ok: false, projectId: null, reason: "not_engineer_invite", path: null };
  }
  const viewer = normEmail(viewerEmail);
  if (!viewer || !intent.emailLower || viewer !== intent.emailLower) {
    return { ok: false, projectId: null, reason: "email_mismatch", path: null };
  }
  return resolvePostInviteProjectLanding({
    inviteProjectIds: [intent.projectId],
    permittedProjectIds,
    requestedProjectId: intent.projectId,
  });
}

/**
 * CL-ACCESS-008: package count = canonical authorized drawing View set.
 */
export function countSharedFilesForCollaborator({
  projectFiles = [],
  fileShares = [],
  email = "",
  uid = "",
  collaborators = null,
  projectId = "",
} = {}) {
  const collaborator = {
    uid: safeStr(uid),
    authUid: safeStr(uid),
    email: safeStr(email),
    emailLower: normEmail(email),
    status: "active",
  };
  const roster =
    Array.isArray(collaborators) && collaborators.length
      ? collaborators
      : [collaborator];
  return countDrawingsInPackageForCollaborator({
    collaborator,
    collaborators: roster,
    projectFileShares: fileShares,
    projectFiles,
    projectId,
  });
}

export function countOpenReviewAssignmentsForCollaborator({
  projectFiles = [],
  email = "",
  uid = "",
} = {}) {
  const emailLower = normEmail(email);
  const authUid = safeStr(uid);
  let count = 0;
  (Array.isArray(projectFiles) ? projectFiles : []).forEach((f) => {
    if (safeStr(f?.status).toLowerCase() === "deleted") return;
    const reqs = Array.isArray(f?.reviewRequests) ? f.reviewRequests : [];
    const hit = reqs.some((r) => {
      if (safeStr(r?.status).toLowerCase() !== "open") return false;
      if (authUid && safeStr(r?.userId) === authUid) return true;
      if (emailLower && normEmail(r?.email) === emailLower) return true;
      return false;
    });
    if (hit) count += 1;
  });
  return count;
}

/**
 * Compact invite readiness for engineer (and reusable for other roles).
 * CL-ENGINEER-LAUNCH-001 — task-based checks; information-only packages are valid.
 */
export function buildEngineerInviteReadinessModel({
  row = null,
  collaboratorsDirty = false,
  projectId = "",
  projectName = "",
  sharedFileCount = 0,
  hasOpenReviewAssignment = false,
  openReviewCount = 0,
  portalVisiblePhotoCount = null,
  canViewPhotos = null,
  shareCommentOkCount = null,
  shareRevisionOkCount = null,
  invitePrepared = false,
  inviteEmailStatus = "",
  hasPortalAccount = false,
  legacyCompatibilityActive = false,
  userRows = [],
} = {}) {
  const email = normEmail(row?.email || row?.emailLower);
  const role = safeStr(row?.role).toLowerCase();
  const scope = safeStr(row?.accessScope).toLowerCase();
  const isEngineer = role === "engineer";
  const perms = normalizeCollaboratorPermissions(row?.permissions, { role, accessScope: scope });
  const photosPerm = canViewPhotos == null ? Boolean(perms.canViewPhotos) : Boolean(canViewPhotos);
  const items = [];
  const warnings = [];
  let blockSend = false;
  let blockReason = "";

  const identityGate = buildEngineerIdentityInviteGate({
    row,
    userRows,
    inviteContactId: safeStr(row?.uid || row?.userId || row?.contactId),
    inviteEmail: email,
  });
  items.push({
    id: "canonical_identity",
    ok: !identityGate.blockSend,
    label: "One canonical Engineer contact",
    detail: identityGate.blockSend
      ? identityGate.blockReason || "Identity conflict"
      : identityGate.canonicalId
        ? `Canonical contact ${identityGate.canonicalId}`
        : email
          ? "CRM-only / manual email OK pre-signup"
          : "Add email",
  });
  if (identityGate.blockSend) {
    blockSend = true;
    blockReason = identityGate.blockReason;
    warnings.push(...(identityGate.warnings || []));
  }

  if (collaboratorsDirty) {
    blockSend = true;
    blockReason = "Save collaborators before creating or sending an invite.";
    items.push({ id: "saved", ok: false, label: "Engineer saved to project", detail: "Unsaved changes" });
  } else {
    items.push({
      id: "saved",
      ok: Boolean(projectId && email && isEngineer),
      label: "Engineer saved to project",
      detail: isEngineer ? "Ready" : `Role is ${roleLabel(role) || "—"}`,
    });
  }

  items.push({
    id: "email",
    ok: Boolean(email),
    label: "Correct email",
    detail: email || "Add an email",
  });
  if (!email) {
    blockSend = true;
    blockReason = blockReason || "Add an email before inviting.";
  }

  items.push({
    id: "portal_account",
    ok: true,
    label: "Portal account status",
    detail: hasPortalAccount ? "Existing portal account detected" : "New account will be created on invite redeem",
  });

  items.push({
    id: "photos",
    ok: photosPerm,
    label: "Project photos configured",
    detail:
      !photosPerm
        ? "Photos permission off"
        : portalVisiblePhotoCount == null
          ? "Photos permission on"
          : portalVisiblePhotoCount > 0
            ? `${portalVisiblePhotoCount} portal-visible photo(s)`
            : "No portal-visible project photos yet",
  });
  if (photosPerm && portalVisiblePhotoCount === 0) {
    warnings.push("No portal-visible project photos");
  }

  const packageOk = sharedFileCount > 0;
  items.push({
    id: "package",
    ok: packageOk,
    label: "Drawings in review package",
    detail: packageOk
      ? `${sharedFileCount} drawing(s) in package`
      : "No drawings in package",
  });
  if (!packageOk) {
    warnings.push("No drawings in package");
  }

  const reviewCount = openReviewCount || (hasOpenReviewAssignment ? 1 : 0);
  items.push({
    id: "review_requests",
    ok: true,
    label: "Open review requests",
    detail:
      reviewCount > 0
        ? `${reviewCount} open`
        : "None — information-only package is valid",
  });
  if (packageOk && reviewCount === 0) {
    warnings.push("Drawings shared but no review request");
  }

  items.push({
    id: "comment",
    ok: Boolean(perms.canReviewDrawings),
    label: "Comment permissions",
    detail:
      shareCommentOkCount != null
        ? `${shareCommentOkCount} share(s) allow comment`
        : perms.canReviewDrawings
          ? "Review/comment enabled"
          : "Review drawings permission off",
  });

  items.push({
    id: "revision",
    ok: Boolean(perms.canUploadFiles),
    label: "Revision/stamped upload permissions",
    detail:
      shareRevisionOkCount != null
        ? `${shareRevisionOkCount} share(s) allow revision upload`
        : perms.canUploadFiles
          ? "Upload enabled"
          : "Upload permission off",
  });
  if (
    packageOk &&
    ((shareCommentOkCount != null && shareCommentOkCount < sharedFileCount) ||
      (shareRevisionOkCount != null && shareRevisionOkCount < sharedFileCount))
  ) {
    warnings.push("Some selected drawings do not allow comment/revision");
  }

  items.push({
    id: "supporting_upload",
    ok: Boolean(perms.canUploadFiles),
    label: "Supporting-document upload permission",
    detail: perms.canUploadFiles ? "Enabled" : "Disabled",
  });

  items.push({
    id: "invite_prepared",
    ok: Boolean(invitePrepared) || Boolean(safeStr(inviteEmailStatus)),
    label: "Invitation prepared",
    detail: invitePrepared || safeStr(inviteEmailStatus) ? "Invite record ready" : "Not prepared yet",
  });

  const emailStatus = safeStr(inviteEmailStatus);
  items.push({
    id: "invite_email",
    ok: true,
    label: "Invitation email status",
    detail: emailStatus || "Not sent",
  });
  if ((invitePrepared || emailStatus) && !/sent/i.test(emailStatus)) {
    warnings.push("Invitation prepared but email not sent");
  }

  if (legacyCompatibilityActive || scope === "full_project") {
    warnings.push(
      "Legacy compatibility metadata remains, but package visibility overrides it"
    );
  }

  const landing =
    projectId && safeStr(projectName)
      ? `${safeStr(projectName)} → /client-portal/projects/${projectId}`
      : projectId
        ? `/client-portal/projects/${projectId}`
        : "Construction portal project list";

  return {
    isEngineer,
    blockSend,
    blockReason,
    warnings,
    items,
    permittedActions: summarizeEngineerPermittedActions(perms, { role, accessScope: scope }),
    landingDestination: landing,
    sharedFileCount,
    informationOnlyPackageValid: packageOk && reviewCount === 0,
  };
}

export function buildEngineerPortalInviteEmail({
  projectName = "Project",
  greetName = "",
  emailLower = "",
  accessCode = "",
  portalBaseUrl = "",
  row = {},
  projectId = "",
  invitedByName = "Mark Beattie",
} = {}) {
  const projectLabel = safeStr(projectName) || "Project";
  const email = normEmail(emailLower);
  const greet =
    safeStr(greetName) ||
    (safeStr(row?.name).split(/\s+/)[0] || (email.includes("@") ? email.split("@")[0] : "there"));
  const code = safeStr(accessCode);
  const portalUrl = resolveClientPortalUrl({ portalBaseUrl, accessCode: code });
  const subject = `${projectLabel} — engineer collaboration invite`;
  // Invite copy uses explicit permission checkboxes — do not let legacy accessScope
  // suppress Photos lines when Mark enabled canViewPhotos for a package engineer.
  const rawPerms = row?.permissions && typeof row.permissions === "object" ? row.permissions : {};
  const canViewPhotos = rawPerms.canViewPhotos === true;
  const canReviewDrawings = rawPerms.canReviewDrawings !== false;
  const canUploadFiles = rawPerms.canUploadFiles !== false;
  const featureLines = [
    "Project overview",
    ...(canViewPhotos ? ["Project photos shared in the portal"] : []),
    "Drawings selected for your review package",
    ...(canReviewDrawings ? ["Comments and review responses where requested"] : []),
    ...(canUploadFiles
      ? ["Revision and stamped-drawing upload where enabled", "Engineering supporting-document upload"]
      : []),
  ];

  let body = `Hi ${greet},\n\n`;
  body += `${safeStr(invitedByName) || "Mark Beattie"} has invited you as an engineer collaborator on ${projectLabel}.\n\n`;
  body += "Your secure project portal contains the current review package. Drawings and documents are viewed in the portal — nothing is attached to this email.\n\n";
  body += "Project access included:\n";
  featureLines.forEach((line) => {
    body += `• ${line}\n`;
  });
  body += "\n";
  body += "Where enabled, you can upload revisions, stamped drawings, and supporting engineering documents from the portal.\n\n";
  body += "To get started:\n";
  body += `1. Open: ${portalUrl}\n`;
  body += "   (Your Project Access Code fills in automatically from this link.)\n";
  body += `2. Create your account or sign in with this email: ${email}\n`;
  body += "   For security, the invited email address is locked and cannot be changed.\n";
  body += "3. Open the project to review your package and respond where requested.\n\n";
  body += "If you have trouble signing in, reply to this email.\n\n";
  body += safeStr(invitedByName) || "Mark Beattie";

  return { subject, body, portalUrl, accessCode: code, featureLines };
}

export function buildEngineerOrientationModel({
  project = null,
  role = "engineer",
  sharedFiles = [],
  filesRequiringReview = [],
  addressLine = "",
} = {}) {
  const projectName = safeStr(project?.name || project?.title) || "This project";
  const address =
    safeStr(addressLine) ||
    safeStr(project?.address) ||
    safeStr(project?.propertyAddress) ||
    safeStr(project?.siteAddress) ||
    "";
  const shared = Array.isArray(sharedFiles) ? sharedFiles : [];
  const needing = Array.isArray(filesRequiringReview) ? filesRequiringReview : [];

  return {
    title: "Engineer collaboration",
    projectName,
    address,
    roleLabel: roleLabel(role) || "Engineer",
    sharedFileCount: shared.length,
    reviewQueueCount: needing.length,
    sharedFileNames: shared.slice(0, 8).map((f) => safeStr(f?.name || f?.fileName || "File")).filter(Boolean),
    reviewFileNames: needing.slice(0, 8).map((f) => safeStr(f?.name || f?.fileName || "File")).filter(Boolean),
    whereToComment: "Open a drawing from Drawings & review (or Needs attention), then use Team discussion to comment.",
    whereToUpload: "Upload revisions or engineering documents from the file review drawer when upload is enabled for that file.",
    emptyReviewNote:
      needing.length === 0
        ? "No files are assigned for your review yet. Mark will share drawings and open a review request when they are ready."
        : null,
    emptySharedNote:
      shared.length === 0
        ? "No drawings are visible for your access yet. If this looks wrong, contact Mark — files may still need to be shared."
        : null,
  };
}
